// Copyright (c) Jonathan Ferraz.
// Licensed under the MIT license.

import { CSSObject, css } from 'styled-components';
import { pxToEm, pxToRem } from 'utils/convertors';

export { pxToEm, pxToRem };

interface Breakpoints {
  [key: string]: string;
}

interface MediaQueries {
  to: {
    [key: string]: (...args: any[]) => any;
  };
  from: {
    [key: string]: (...args: any[]) => any;
  };
}

export const defaultBreakpoints: Breakpoints = {
  huge: '1440px',
  large: '1199.98px',
  medium: '991.98px',
  small: '767.98px',
  xsmall: '575.98px',
};

function getSizeFromBreakpoint(
  breakpointValue: any,
  breakpoints: Breakpoints = {}
) {
  if (breakpoints[breakpointValue]) {
    return breakpoints[breakpointValue];
  } else if (parseInt(breakpointValue)) {
    return breakpointValue;
  } else {
    return '0';
  }
}

export function generateMedia(breakpoints: Breakpoints = defaultBreakpoints) {
  const lessThan =
    (breakpoint: string | number) =>
    (...args: (CSSObject | TemplateStringsArray)[]) => {
      const styles = args.map((arg) => String(arg)).join('');

      return css`
        @media (max-width: ${getSizeFromBreakpoint(breakpoint, breakpoints)}) {
          ${styles}
        }
      `;
    };

  const greaterThan =
    (breakpoint: string | number) =>
    (...args: (CSSObject | TemplateStringsArray)[]) => {
      const styles = args.map((arg) => String(arg)).join('');

      return css`
        @media (min-width: ${getSizeFromBreakpoint(breakpoint, breakpoints)}) {
          ${styles}
        }
      `;
    };

  const between =
    (firstBreakpoint: string | number, secondBreakpoint: string | number) =>
    (...args: (CSSObject | TemplateStringsArray)[]) => {
      const styles = args.map((arg) => String(arg)).join('');

      return css`
        @media (min-width: (${getSizeFromBreakpoint(
            firstBreakpoint,
            breakpoints
          )})) and (max-width: (${getSizeFromBreakpoint(
            secondBreakpoint,
            breakpoints
          )})) {
          ${styles}
        }
      `;
    };

  const oldStyle = (breakpoints: Breakpoints) => {
    return Object.keys(breakpoints).reduce(
      (acc: MediaQueries, label: string) => {
        const size = breakpoints[label];

        acc.to[label] = (...args: (CSSObject | TemplateStringsArray)[]) => {
          const styles = args.map((arg) => String(arg)).join('');
          return css`
            @media (max-width: ${size}) {
              ${styles}
            }
          `;
        };

        acc.from[label] = (...args: (CSSObject | TemplateStringsArray)[]) => {
          const styles = args.map((arg) => String(arg)).join('');
          return css`
            @media (min-width: ${size}) {
              ${styles}
            }
          `;
        };

        return acc;
      },
      { to: {}, from: {} }
    );
  };

  return Object.assign(
    {
      lessThan,
      greaterThan,
      between,
    },
    oldStyle
  );
}

export default generateMedia();
